import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import style from '../common/SettingsList.module.scss';
import Layout from '../../common/Layout/Layout';
import Table from '../../common/table/Table';
import Empty from '../../common/empty/Empty';
import ButtonCommon from '../../common/button/ButtonCommon';
import Pagination from '../../common/pagination/Pagination';
import dayjs from '../../../util/dayjs';
import {
  AdministratorAccessLogFilter,
  getAdministratorAccessLogs,
  getAdministratorOptions,
} from '../../../api/function/administrator';
import { AdministratorAccessLogDto, AdministratorOptionDto } from '../../../api/function/administrator/rs';
import useServerPaging from '../common/useServerPaging';
import ClampText from '../common/ClampText';

const cx = classNames.bind(style);
const ACTION_TYPES: { value: string; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'LOGIN', label: '로그인' },
  { value: 'LOGIN_FAIL', label: '로그인 실패' },
  { value: 'LOGOUT', label: '로그아웃' },
  { value: 'READ', label: '조회' },
  { value: 'CREATE', label: '등록' },
  { value: 'UPDATE', label: '수정' },
  { value: 'DELETE', label: '삭제' },
  { value: 'DOWNLOAD', label: '다운로드' },
];

// 기본 조회 범위는 지난 1개월(월 1회 접속기록 점검 단위)
const defaultFilter = (): AdministratorAccessLogFilter => ({
  fromDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
  toDate: dayjs().format('YYYY-MM-DD'),
  actionType: '',
});

// 로그인 실패는 기록 자체가 실패 이벤트라 응답 코드와 무관하게 실패로 본다
const isSuccess = (log: AdministratorAccessLogDto) =>
  log.actionType !== 'LOGIN_FAIL' && (log.responseStatus === null || log.responseStatus < 400);

const AccessLogList = () => {
  const [administrators, setAdministrators] = useState<AdministratorOptionDto[]>([]);
  const [draft, setDraft] = useState<AdministratorAccessLogFilter>(defaultFilter);
  const [filter, setFilter] = useState<AdministratorAccessLogFilter>(defaultFilter);
  const [logs, setLogs] = useState<AdministratorAccessLogDto[]>([]);
  const { page, setPage, applyResult, resetToFirst } = useServerPaging();

  useEffect(() => {
    getAdministratorOptions().then((rs) => setAdministrators(rs.data));
  }, []);

  useEffect(() => {
    getAdministratorAccessLogs(filter, page.page, page.size)
      .then((rs) => {
        setLogs(rs.data.list);
        applyResult(rs.data.pagination);
      })
      .catch((e) => alert(e?.response?.data?.data?.cause ?? '관리자 활동 기록을 불러오지 못했습니다.'));
  }, [filter, page.page, page.size]);

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    resetToFirst();
    setFilter({ ...draft });
  };

  return (
    <Layout>
      <h2>관리자 활동 기록</h2>
      <p className={cx('description')}>
        관리자 계정의 로그인·로그아웃, 화면 조회, 등록·수정·삭제, 다운로드 기록입니다(ISMS 개인정보처리시스템 접속기록). 월 1회
        이상 점검하고, 기록은 2년 이상 보관됩니다.
      </p>
      <form className={cx('toolbar')} onSubmit={search}>
        <div className={cx('field')}>
          <label htmlFor={'access-log-from'}>기간</label>
          <div className={cx('range')}>
            <input
              id={'access-log-from'}
              type={'date'}
              value={draft.fromDate ?? ''}
              onChange={(e) => setDraft({ ...draft, fromDate: e.target.value })}
            />
            <span>~</span>
            <input
              id={'access-log-to'}
              type={'date'}
              aria-label={'기간 끝'}
              value={draft.toDate ?? ''}
              onChange={(e) => setDraft({ ...draft, toDate: e.target.value })}
            />
          </div>
        </div>
        <div className={cx('field')}>
          <label htmlFor={'access-log-administrator'}>관리자</label>
          <select
            id={'access-log-administrator'}
            value={draft.administratorId ?? ''}
            onChange={(e) =>
              setDraft({ ...draft, administratorId: e.target.value ? Number(e.target.value) : undefined })
            }
          >
            <option value={''}>전체</option>
            {administrators.map((administrator) => (
              <option key={administrator.id} value={administrator.id}>
                {administrator.name} ({administrator.email})
              </option>
            ))}
          </select>
        </div>
        <div className={cx('field')}>
          <label htmlFor={'access-log-action'}>구분</label>
          <select
            id={'access-log-action'}
            value={draft.actionType ?? ''}
            onChange={(e) => setDraft({ ...draft, actionType: e.target.value })}
          >
            {ACTION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <ButtonCommon size={'small'} submitType>
          조회
        </ButtonCommon>
      </form>
      <Table className={cx('table')}>
        <colgroup>
          <col style={{ width: '12%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '6%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>일시</th>
            <th>관리자</th>
            <th>IP</th>
            <th>구분</th>
            <th>메뉴 · 업무</th>
            <th>대상(정보주체·조건)</th>
            <th>사유</th>
            <th>결과</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => {
            const success = isSuccess(log);
            return (
              <tr key={log.id}>
                <td>
                  {dayjs(log.createdDateTime).format('YYYY.MM.DD')}
                  <span className={cx('sub')}>{dayjs(log.createdDateTime).format('HH:mm:ss')}</span>
                </td>
                <td>
                  {log.administratorName ?? '-'}
                  <span className={cx('sub')}>{log.administratorEmail}</span>
                </td>
                <td>{log.ip}</td>
                <td>{log.actionTypeName}</td>
                <td className={cx('left')}>
                  {[log.menu, log.action].filter(Boolean).join(' · ') || '-'}
                  <span className={cx('sub')}>
                    <ClampText text={`${log.httpMethod} ${log.requestUri}`} />
                  </span>
                </td>
                <td className={cx('left')}>
                  <ClampText text={log.target} />
                </td>
                <td className={cx('left')}>
                  <ClampText text={log.reason} />
                </td>
                <td>
                  <span
                    className={cx('badge', success ? 'ok' : 'fail')}
                    title={log.responseStatus !== null ? `응답 코드 ${log.responseStatus}` : undefined}
                  >
                    {success ? '정상' : '실패'}
                  </span>
                </td>
              </tr>
            );
          })}
          {!logs.length && (
            <tr>
              <td colSpan={8}>
                <Empty />
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Pagination page={page} countWord={'건'} pageHandler={setPage} />
    </Layout>
  );
};

export default AccessLogList;
